import type { MouseEventHandler } from 'react';
import React, { useState } from 'react';

import styled, { css } from 'styled-components';
import { DeleteItem } from '@lt/components/icons-react/userflow';
import type {
  OptionState,
  SavedTourist,
} from 'src/routes/Checkout/store/client/types';
import { useTranslation } from '@lt/localization';
import TouristInfo from '../TouristInfo';
import TouristOptionButtons from '../TouristOptionButtons';
import {
  TEST_ID_TOURIST_NAME_OPTION,
  TEST_ID_TOURIST_NAME_OPTION_DELETE_ICON,
} from './constants';

type Props = {
  isMobile: boolean;
  onClickOption: () => void;
  data: SavedTourist;
  deleteSavedTourist: (tourist: SavedTourist) => void;
};

const SavedTouristOptionBody = (props: Props) => {
  const { onClickOption, deleteSavedTourist, isMobile, data } = props;
  const { name, surname, gender, birthday, passport_number, state } = data;

  const [hasDeleteAttempt, setHasDeleteAttempt] = useState(false);

  const { t } = useTranslation();

  const handleConfirmDelete: MouseEventHandler<HTMLButtonElement> = (e) => {
    e.stopPropagation();
    deleteSavedTourist(data);
  };

  const handleAttemptDelete: MouseEventHandler<SVGSVGElement> = (e) => {
    e.stopPropagation();
    setHasDeleteAttempt(true);
  };

  const handleCancelDelete: MouseEventHandler<HTMLButtonElement> = (e) => {
    e.stopPropagation();
    setHasDeleteAttempt(false);
  };

  const handleClick = () => {
    onClickOption();
  };

  return (
    <Option
      onClick={handleClick}
      $state={state}
      $hasDeleteAttempt={hasDeleteAttempt}
      data-testid={TEST_ID_TOURIST_NAME_OPTION}
    >
      <ContentSection>
        {!hasDeleteAttempt && state === 'normal' && (
          <TouristInfo
            birthday={birthday}
            name={name}
            surname={surname}
            passport_number={passport_number}
            gender={gender}
          />
        )}
        {(hasDeleteAttempt || state === 'deleting') && (
          <DeletePrompt>
            {t('checkout.touristForm.name.removeTourist')}
          </DeletePrompt>
        )}
        {state === 'error' && (
          <TryAgainPrompt>
            {t('checkout.touristForm.name.errorMessage')}
          </TryAgainPrompt>
        )}
      </ContentSection>
      <ButtonSection>
        {!hasDeleteAttempt && state === 'normal' && (
          <DeleteIcon
            width={24}
            height={24}
            onClick={handleAttemptDelete}
            data-testid={TEST_ID_TOURIST_NAME_OPTION_DELETE_ICON}
          />
        )}
        {(hasDeleteAttempt || state === 'deleting' || state === 'error') && (
          <TouristOptionButtons
            handleCancelDelete={handleCancelDelete}
            handleConfirmDelete={handleConfirmDelete}
            state={state}
            isMobile={isMobile}
          />
        )}
      </ButtonSection>
    </Option>
  );
};

const DeletePrompt = styled.div`
  max-width: 104px;
`;

const TryAgainPrompt = styled.div`
  max-width: 135px;
`;

const DeleteIcon = styled(DeleteItem)`
  margin-right: 8px;
  cursor: pointer;
`;

const Option = styled.div<{ $state?: OptionState; $hasDeleteAttempt: boolean }>`
  display: flex;
  align-items: center;
  width: 100%;
  min-width: 320px;
  justify-content: space-between;
  padding: 16px 8px;
  color: black;

  background-color: white;

  font-size: 14px;
  line-height: 16px;

  &:hover {
    background: #e6f2ff;
  }

  ${(props) =>
    (props.$hasDeleteAttempt || props.$state === 'deleting') &&
    css`
      background-color: #fffbe5;
      padding: 12px 8px 12px 16px;
    `}

  ${(props) =>
    props.$state === 'error' &&
    css`
      background-color: #f9eaec;
      padding: 12px 8px 12px 16px;
    `}

  border-bottom: 1px solid #E6E9EE;

  &:last-child {
    border-bottom: none;
  }
`;

const ContentSection = styled.div`
  display: flex;
`;

const ButtonSection = styled.div`
  display: flex;
  align-items: center;
`;

export default SavedTouristOptionBody;
